import React, { useMemo, useState } from 'react';
import { DynamicFormFrame } from '../form/dynamic-form/DynamicFormFrame';
import { useStateProgramContext } from './StateProgramContext';
import { Translator } from '@jsonforms/core';
import { useLocalization } from '../../store/utils/useLocalization';
import { pureTranslate } from '../../utils/localization';
import { languageCode } from '../../types/Languages';

interface StateProgramFormFrameProps {
  /**
   * Observer for when form changes.
   * Does not override internal handling of changes, and changes
   * will still get saved to program data.
   */
  onChange?: () => void;
}

export function StateProgramFormFrame(props: StateProgramFormFrameProps) {
  const programServices = useStateProgramContext();

  // seed the program with any existing state but track it separately
  // so that we maintain the state even if the form doesn't validate
  const [unsavedData, setUnsavedData] = useState(programServices.frameAttestationData);

  const { locale } = useLocalization();
  const createTranslator = (locale: languageCode): Translator => {
    // function overloads to match the JSON Forms Translator type
    function translator(key: string, defaultMessage: string, params?: any): string;
    function translator(key: string, defaultMessage: undefined, params?: any): string | undefined;
    function translator(key: string, defaultMessage?: string, params?: any): string | undefined;
    function translator(key: string, defaultMessage?: string, params?: any): string | undefined {
      console.trace('Form translator', { key, defaultMessage, params });
      return pureTranslate(
        locale,
        programServices.frameTranslations,
        key,
        // JSON Forms has an evaluation order for what gets passed as a default message:
        // @see https://jsonforms.io/docs/i18n/#evaluation-order
        // The error from the validator should get passed as the last resort.
        defaultMessage,
        params
      );
    }
    return translator;
  };
  const translate = useMemo(() => createTranslator(locale), [locale]);

  return (
    <DynamicFormFrame
      i18n={{ locale: locale, translate: translate }}
      schema={programServices.frameAttestationSchema}
      uischema={programServices.frameUiSchema}
      // update local state and try to save form if it validates
      data={unsavedData}
      onChange={(state) => {
        setUnsavedData(state.data);
        programServices.changeFrameData(state);
        if (props.onChange !== undefined) {
          props.onChange();
        }
      }}
      // use validation from Wizard Engine instead of JSON Forms built-in
      validationMode="NoValidation"
      additionalErrors={
        programServices.frameUpdateStatus?.success ? [] : programServices.frameUpdateStatus?.errors
      }
    />
  );
}
